/**
 * Unified file URL helpers for enpiso-admin-front.
 * Mirrors the helpers in enpiso-front/src/utils/fileUrl.js.
 *
 * A `file` object from the API has this shape:
 *   { id, url, path, variants: { thumb: "...", medium: "..." }, mime_type, ... }
 *
 * If `file.url` is present (S3/CDN/MinIO public), use it directly.
 * Otherwise route through the proxy endpoint served by enpiso-admin-back.
 */

const API_ORIGIN = (() => {
  let base = process.env.REACT_APP_API_SERVER || window.location.origin;
  base = base.replace(/\/api\/v\d+\/?$/i, '').replace(/\/api\/?$/i, '');
  return base.replace(/\/$/, '');
})();

/**
 * Get the URL for a file object returned by the API.
 */
export const fileUrl = (file, variant = 'original') => {
  if (!file) return null;

  if (variant !== 'original' && file.variants?.[variant]) {
    const v = file.variants[variant];
    if (typeof v === 'string' && (v.startsWith('http') || v.startsWith('/'))) return v;
    return `${API_ORIGIN}/api/v1/files/${file.id}/serve?v=${variant}`;
  }

  if (file.url) return file.url;

  if (file.id) {
    return variant === 'original'
      ? `${API_ORIGIN}/api/v1/files/${file.id}/serve`
      : `${API_ORIGIN}/api/v1/files/${file.id}/serve?v=${variant}`;
  }

  return null;
};

/**
 * Get the thumbnail URL for a file object.
 */
export const thumbUrl = (file) => {
  if (!file) return null;
  if (file.variants?.thumb) return fileUrl(file, 'thumb');
  if (file.id) return `${API_ORIGIN}/api/v1/files/${file.id}/thumb`;
  return fileUrl(file);
};

/**
 * Get the download URL for a file (forces browser download).
 */
export const downloadUrl = (file) => {
  if (!file?.id) return null;
  return `${API_ORIGIN}/api/v1/files/${file.id}/download`;
};

/**
 * Build a legacy storage URL for paths stored directly in DB (pre-migration).
 */
export const legacyStorageUrl = (subPath) => {
  if (!subPath) return null;
  if (subPath.startsWith('http://') || subPath.startsWith('https://')) return subPath;
  if (subPath.startsWith('data:')) return subPath;
  const cleanPath = subPath.replace(/^\/?(storage\/)?/, '');
  return `${API_ORIGIN}/storage/${cleanPath}`;
};
